import { isHeartbeatPromptText } from "../../../../src/auto-reply/heartbeat.js";
import {
  extractBackgroundTaskResultBody,
  extractBackgroundTaskResultLabel,
  extractConversationSummary,
  hasConversationSummary,
  isOperatorFacingInjectText,
  isOperatorFacingTranscriptMessage,
  isOperatorMilestoneInjectText,
  resolveChatHeartbeatActive,
  shouldOmitChatToolTranscriptMessage,
  shouldOmitChatTranscriptMessage,
  stripConversationSummary,
  stripContinuityFromUserText,
  stripOperatorInjectDisplayText,
  stripUserFacingChatText,
  stripOrchestrationBlocksFromAssistantReplies,
} from "../../../../src/chat/transcript-filters.js";
import {
  hasOrchestrationInfrastructureMarkers,
  stripOrchestrationInfrastructure,
} from "../../../../src/orchestration/orchestration-transcript.js";
import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import type { AssistantTurn } from "./chat-assistant-turn.ts";
import { messageTextContent } from "./fleet-chat-send-options.ts";

export {
  extractBackgroundTaskResultBody,
  extractBackgroundTaskResultLabel,
  extractConversationSummary,
  hasConversationSummary,
  hasOrchestrationInfrastructureMarkers,
  isOperatorFacingInjectText,
  isOperatorFacingTranscriptMessage,
  isOperatorMilestoneInjectText,
  resolveChatHeartbeatActive,
  shouldOmitChatToolTranscriptMessage,
  shouldOmitChatTranscriptMessage,
  stripConversationSummary,
  stripOperatorInjectDisplayText,
  stripOrchestrationBlocksFromAssistantReplies,
  stripOrchestrationInfrastructure,
  stripUserFacingChatText,
};

export function stripContinuityFromUserMessage(message: unknown): string {
  return stripContinuityFromUserText(messageTextContentRaw(message));
}

function messageTextContentRaw(message: unknown): string {
  const m = message as Record<string, unknown>;
  if (typeof m.content === "string") {
    return m.content;
  }
  if (Array.isArray(m.content)) {
    return m.content
      .map((item) => {
        const x = item as Record<string, unknown>;
        return typeof x.text === "string" ? x.text : "";
      })
      .join("\n");
  }
  if (typeof m.text === "string") {
    return m.text;
  }
  return "";
}

export { isHeartbeatPromptText };

function replaceMessageText(message: unknown, nextText: string): unknown {
  const m = message as Record<string, unknown>;
  if (typeof m.content === "string") {
    return { ...m, content: nextText };
  }
  if (Array.isArray(m.content)) {
    const next = m.content.map((item, index) => {
      if (index !== 0) {
        return item;
      }
      const entry = item as Record<string, unknown>;
      if (entry.type === "text" && typeof entry.text === "string") {
        return { ...entry, text: nextText };
      }
      return item;
    });
    return { ...m, content: next };
  }
  if (typeof m.text === "string") {
    return { ...m, text: nextText };
  }
  return message;
}

export function stripUserFacingChatMessage(message: unknown): string {
  return stripUserFacingChatText(messageTextContentRaw(message));
}

/**
 * Strip continuity / orchestration for display, but keep prompt-budget summary
 * blocks so the transcript can render them as a muted collapsible chip.
 */
export function patchUserMessageForDisplay(message: unknown): unknown {
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  if (role !== "user") {
    return message;
  }
  const raw = messageTextContentRaw(message);
  const { summary, body } = extractConversationSummary(raw);
  const strippedBody = stripContinuityFromUserText(stripOrchestrationInfrastructure(body)).trim();
  const nextText = summary
    ? strippedBody
      ? `${summary}\n\n${strippedBody}`
      : summary
    : strippedBody;
  if (!nextText || nextText === raw.trim()) {
    return message;
  }
  return replaceMessageText(message, nextText);
}

// ---------------------------------------------------------------------------
// JAISIU-3329 — UX-R4: Always-visible conversation.
//
// The chat transcript used to apply a flat "show the last N=200 messages"
// cutoff via `resolveChatHistoryPaging`. That truncated the underlying
// `props.messages` array BEFORE the items were built, which meant the
// "Load older messages" button could hide the user's most recent prompt
// and the assistant's final answer — the two bubbles the user actually
// cares about. The ticket asks for a content-aware partition: user prompts
// and final-assistant bubbles must NEVER be hidden by the cutoff. Only
// activity rows (tool calls, subagent runs, environment probes, system
// status) may be collapsed under a capped Activity section.
//
// `MessageOriginKind` is the input shape for that decision, and
// `partitionTranscriptForVisibility` is the pure partitioner. The chat
// view calls it after `buildChatItems`; the output drives whether the
// "Load older messages" button renders at all (it disappears entirely
// when no activity rows are eligible to be hidden).
// ---------------------------------------------------------------------------

/**
 * Classification of a single chat row for the always-visible-conversation
 * cutoff.
 *
 *  - "user-prompt"          — a user-role message bubble.
 *  - "assistant-final"      — a final assistant bubble (either an
 *                             `AssistantTurn` row, a single
 *                             assistant-role message/group, or a
 *                             assistant-role message inside a group).
 *                             These are the bubbles that MUST stay
 *                             visible regardless of transcript length.
 *  - "system"               — operator-facing system notices
 *                             (file-changes / fleet-complete /
 *                             fleet-result). These surface as their
 *                             own rows but are not a "final answer".
 *  - "activity"             — anything else: tool calls, tool
 *                             results, subagent runs, orchestration
 *                             groups, live status, reading
 *                             indicators, streams, compactions.
 *                             These are the only rows that may be
 *                             hidden by the load-more cutoff.
 */
export type MessageOriginKind = "user-prompt" | "assistant-final" | "system" | "activity";

export type ChatItemOrGroup = ChatItem | MessageGroup | AssistantTurn;

export interface PartitionedTranscript<
  TConversation = ChatItemOrGroup,
  TActivity = ChatItemOrGroup,
> {
  /**
   * Items that must always remain visible in the chat thread. Never
   * truncated by the load-more cutoff.
   */
  alwaysVisible: TConversation[];
  /**
   * Activity items (tool calls, subagent work, environment probes,
   * compaction notices, system status). These MAY be hidden by the
   * load-more cutoff when the transcript is too long. The chat view
   * caps them at `activityCap` rows; older activity rows are
   * summarized behind a collapsible Activity section.
   */
  collapsibleActivity: TActivity[];
}

/**
 * Default cap for the collapsible Activity section. Mirrors the
 * existing `CHAT_HISTORY_RENDER_LIMIT` of 200 but is applied ONLY to
 * activity rows, not to the conversation rows above them. 25 is a
 * conservative starting point that keeps the visible thread readable
 * while still letting the user expand a longer Activity log on
 * demand.
 */
export const DEFAULT_ACTIVITY_CAP = 25;

export function classifyChatRowOrigin(item: ChatItemOrGroup): MessageOriginKind {
  switch (item.kind) {
    case "message":
      return classifyMessageByRole((item as { message: unknown }).message);
    case "group":
      return classifyGroupByRole(item.role);
    case "assistant-turn":
      // An `AssistantTurn` always carries the canonical final answer
      // (its `finalAnswer` field). The collapsible activity rows for
      // the turn are NESTED inside the turn's own UI (the
      // Activity box rendered by `renderChatLiveRunGroup`), so the
      // outer row is itself "conversation" — final-answer-bearing
      // content the user must see.
      return "assistant-final";
    case "file-changes":
    case "fleet-complete":
    case "fleet-result":
    case "subagent-results":
      // Operator-facing system notices — surface as their own rows
      // but they are not "the assistant's final answer". Classify
      // them as system so the cutoff treats them like activity
      // (collapsible, capped) but keeps them visibly distinct from
      // tool/subagent activity in tests/logs. `subagent-results`
      // is the collapsed form of `fleet-result` rows, so it inherits
      // the same classification — otherwise collapsing a lone report
      // would silently demote it from system to activity.
      return "system";
    case "stream":
    case "live-run":
    case "reading-indicator":
    case "subagent-run":
    case "orchestration-group":
    case "minimal-status":
      return "activity";
    default: {
      // Defensive: any future `ChatItem` kind defaults to
      // "activity" (collapsible). We deliberately do NOT throw —
      // adding a new kind to `ChatItem` should not crash the
      // cutoff.
      return "activity";
    }
  }
}

function classifyMessageByRole(message: unknown): MessageOriginKind {
  const m = message as Record<string, unknown>;
  const role = typeof m.role === "string" ? m.role.toLowerCase() : "";
  if (role === "user") return "user-prompt";
  if (role === "assistant") return "assistant-final";
  if (role === "tool" || role === "toolresult") return "activity";
  if (role === "system") return "system";
  // Unknown roles (e.g. custom plugin roles) default to "activity"
  // so they remain collapsible; that is the safest fallback for
  // any new role added without an explicit classification.
  return "activity";
}

function classifyGroupByRole(role: string): MessageOriginKind {
  const normalized = role.toLowerCase();
  if (normalized === "user") return "user-prompt";
  if (normalized === "assistant") return "assistant-final";
  if (normalized === "tool" || normalized === "toolresult") return "activity";
  if (normalized === "system") return "system";
  return "activity";
}

/**
 * Pure partitioner — given a flat array of chat items (the output of
 * `buildChatItems`), split them into:
 *
 *  - `alwaysVisible`: user prompts + final assistant bubbles. The chat
 *    view renders these WITHOUT any cutoff, even when the underlying
 *    history has thousands of rows.
 *  - `collapsibleActivity`: tool calls, subagent runs, environment
 *    probes, system notices. The chat view caps these at `activityCap`
 *    rows; rows beyond the cap are summarized behind a collapsible
 *    Activity section.
 *
 * Input order is preserved within each output array. The function is
 * pure: it does not mutate `items`.
 *
 * Invariants:
 *  1. `alwaysVisible` + `collapsibleActivity` covers every element of
 *     `items` exactly once.
 *  2. The LAST user prompt and the LAST assistant-final bubble from
 *     `items` are always present in `alwaysVisible` (even when there
 *     are 200+ activity rows).
 *  3. The partitioner does NOT itself apply the activity cap — that
 *     is the caller's job. We return the FULL activity list so the
 *     caller can decide whether to render a "Load older activity"
 *     disclosure.
 */
export function partitionTranscriptForVisibility(
  items: ReadonlyArray<ChatItemOrGroup>,
): PartitionedTranscript {
  const alwaysVisible: ChatItemOrGroup[] = [];
  const collapsibleActivity: ChatItemOrGroup[] = [];
  for (const item of items) {
    const origin = classifyChatRowOrigin(item);
    if (origin === "user-prompt" || origin === "assistant-final") {
      alwaysVisible.push(item);
    } else {
      collapsibleActivity.push(item);
    }
  }
  return { alwaysVisible, collapsibleActivity };
}

/**
 * Convenience: cap the activity list at `activityCap` rows and report
 * whether any rows were dropped. The caller renders the dropped rows
 * behind a collapsible "Activity" section; the dropped count is
 * surfaced as a `+ N earlier` hint inside that section.
 */
export interface ActivityCapResult {
  visible: ChatItemOrGroup[];
  hiddenCount: number;
}

export function capActivityRows(
  rows: ReadonlyArray<ChatItemOrGroup>,
  activityCap: number = DEFAULT_ACTIVITY_CAP,
): ActivityCapResult {
  if (rows.length <= activityCap) {
    return { visible: rows.slice(), hiddenCount: 0 };
  }
  return {
    visible: rows.slice(-activityCap),
    hiddenCount: rows.length - activityCap,
  };
}
